import React from 'react';
import { fiscalYearOf } from './primitives';

/**
 * Executive callouts — the 2–4 facts that must be impossible to miss.
 * Every callout is derived verbatim or arithmetically from payload fields;
 * nothing here is generated or inferred.
 */
const buildCallouts = (analysis) => {
  const { distressScore, ohlsonScore, distressAgreement, trend } = analysis;
  const callouts = [];

  if (trend && trend.available) {
    const years = `${fiscalYearOf(trend.firstPeriod)}\u2013${fiscalYearOf(trend.lastPeriod)}`;
    if (trend.overall === 'Deteriorating') {
      callouts.push({
        tone: 'medium',
        glyph: '\u26A0',
        text: `Book-equity Z deteriorated over ${years} (year-to-year series, not the Condition score)`
      });
    } else if (trend.overall === 'Improving') {
      callouts.push({
        tone: 'low',
        glyph: '\u2713',
        text: `Book-equity Z improved over ${years} (year-to-year series, not the Condition score)`
      });
    } else if (trend.overall === 'Stable') {
      callouts.push({
        tone: 'low',
        glyph: '\u2713',
        text: `Book-equity Z held steady over ${years} (year-to-year series, not the Condition score)`
      });
    }
  }

  if (ohlsonScore && ohlsonScore.oScore !== null && ohlsonScore.band) {
    const tone = ohlsonScore.band === 'High' ? 'high' : ohlsonScore.band === 'Moderate' ? 'medium' : 'low';
    callouts.push({
      tone,
      glyph: tone === 'low' ? '\u2713' : '\u26A0',
      text: `Distress probability ${ohlsonScore.band.toLowerCase()} at ${ohlsonScore.probabilityOfDistress}%`
    });
  }

  if (distressAgreement && distressAgreement.status && distressAgreement.status !== 'Indeterminate') {
    const status = distressAgreement.status;
    if (status === 'Agree') {
      callouts.push({
        tone: 'low',
        glyph: '\u2713',
        text: 'Both distress models point the same way'
      });
    } else if (status === 'Partially agree') {
      callouts.push({
        tone: 'medium',
        glyph: '\u26A0',
        text: 'Distress models differ by one band — read both'
      });
    } else {
      callouts.push({
        tone: 'medium',
        glyph: '\u26A0',
        text: 'Distress models diverge — read the evidence'
      });
    }
  }

  if (distressScore.riskZone === 'Distress Zone') {
    callouts.push({ tone: 'high', glyph: '\u26A0', text: 'Score sits in the high-risk band' });
  }

  return callouts.slice(0, 4);
};

const Callouts = ({ analysis }) => {
  const callouts = buildCallouts(analysis);
  if (callouts.length === 0) return null;

  return (
    <div className="fr-callouts">
      {callouts.map((callout, index) => (
        <span key={index} className={`fr-callout fr-callout-${callout.tone}`}>
          <span className="fr-callout-glyph" aria-hidden="true">
            {callout.glyph}
          </span>
          {callout.text}
        </span>
      ))}
    </div>
  );
};

export default Callouts;
